import { app } from '@/main';
import {
  EBusinessIdentify,
  EDevice,
  EUtmParamsEvent,
  UTM_DATA_STORAGE
} from '@/models/segment.io';
import { deviceType } from '@/utils/deviceType';
import APIService from '@/api';
import { IBusinessType } from '@/models/business';
import flagKeys from '@/constants/flagKeys';
import { maskPiiFields } from '@twm-libs/utils';
import logger from './logger';
import { merchantoauthSessionKeys } from '@/constants/merchantoauth';
import isRemoteLoading from './remote-loading';

const insideProviderIframe = () => {
  const rawAncestors = window.location.ancestorOrigins || [];
  const ancestorOrigins = Array.from(rawAncestors);

  const { providers } = merchantoauthSessionKeys.connect;

  const inIFrame = ancestorOrigins.some(ancestor => {
    const providersNames = Object.keys(providers) as Array<
      keyof typeof providers
    >;

    return providersNames.some(key =>
      ancestor.includes(providers[key].domain as string)
    );
  });

  if (inIFrame) return true;

  return window.self !== window.top;
};

interface NavigatorProps extends Navigator {
  userAgentData: any;
}

interface ISegmentScriptProps {
  isImpersonated?: boolean;
  isSuperAdmin?: boolean;
  featureFlags: Array<string>;
}

export const TRACKING_DEBUGGER_STORAGE_KEY =
  '@twism:tracking-debugger-is-active';

export const TRACKING_ENABLE_STORAGE_KEY = '@twism:tracking-enable-is-active';

export const shouldShowTrackingDebugger = () =>
  !['production', 'staging'].includes(process.env.VUE_APP_ENV || '');

export const trackingEnableIsActive = () =>
  JSON.parse(localStorage.getItem(TRACKING_ENABLE_STORAGE_KEY) || 'false');

export const trackingDebuggerIsActive = () =>
  JSON.parse(localStorage.getItem(TRACKING_DEBUGGER_STORAGE_KEY) || 'false');

function analyticsDebugger(message: string) {
  if (shouldShowTrackingDebugger() && trackingDebuggerIsActive()) {
    app.$notification(message, 'info', 5000); // 5s
  }
}

// Split like the backend's buildFullname: first token is the name, the rest is
// the surname. These are the only writers of the two traits — the backend's
// profile-property identify carries `email` but no name (TWISM-18988).
//
// Same manager as the `email` on this identify, deliberately: these land on a
// business-keyed profile and are last-write-wins across a business's managers,
// so name and email have to describe the same person or a personalised mail
// greets the wrong one. Owner identity is `Owner ID`, not this.
function managerNameTraits(manager: any) {
  if (!manager || !manager.fullname) return {};

  const [first, ...rest] = String(manager.fullname)
    .trim()
    .split(' ');

  return {
    [EBusinessIdentify.FIRST_NAME]: first,
    ...(rest.length && { [EBusinessIdentify.LAST_NAME]: rest.join(' ') })
  };
}

function sanitizeData(data: any) {
  if (!app.$store.getters['flags/getFlags'].includes(flagKeys.segmentSanitizer))
    return data;

  const businessInfo = app.$store.getters['business/getBusinessInfo'];
  const { properties } = maskPiiFields({
    properties: data,
    userId:
      businessInfo && businessInfo.business ? businessInfo.business.id : '',
    event: ''
  });
  return properties;
}

export const addSegmentScript = async ({
  isImpersonated,
  isSuperAdmin,
  featureFlags
}: ISegmentScriptProps) => {
  const shouldAddSegmentScript = !isImpersonated && !isSuperAdmin;

  const segmentScriptId = 'segment-script';

  if (
    (window.analytics && typeof window.analytics === 'function') ||
    !shouldAddSegmentScript ||
    document.querySelector(`#${segmentScriptId}`)
  )
    return;

  const script = document.createElement('script');

  const segmentId = process.env.VUE_APP_SEGMENT_ID;
  const segmentIdNew = process.env.VUE_APP_SEGMENT_ID_NEW;

  let token = segmentId;

  if (segmentIdNew && featureFlags.includes(flagKeys.newSegmentForImport)) {
    token = segmentIdNew;
  }

  const scriptCode = `
    !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=e};analytics._writeKey="${token}";;analytics.SNIPPET_VERSION="4.15.3";
    analytics.load("${token}");
    }}();
  `;
  script.id = segmentScriptId;
  script.innerText = scriptCode;

  try {
    document.body.appendChild(script);
    if (window.analytics && window.analytics.ready) {
      window.analytics.ready(() => {
        if (window.analytics && window.analytics.user) {
          logger.setAnonymousId(window.analytics.user().anonymousId());
        }
      });
    }
  } catch (error) {
    logger.info('Segment snippet error', error);
  }
};

const segmentIo = {
  normalizeEventLabel(label: string) {
    return label.replace(/\//gi, ' | ');
  },
  async getDeviceInfos() {
    const { userAgentData = null } = window.navigator as NavigatorProps;

    let deviceInfos;

    if (userAgentData) {
      deviceInfos = await userAgentData.getHighEntropyValues([
        'model',
        'platformVersion'
      ]);
    }

    return {
      [EDevice.RESOLUTION]: `${window.screen.width}x${window.screen.height}`,
      [EDevice.RESOLUTION_VIEWPORT]: `${window.innerWidth}x${
        window.innerHeight
      }`,
      [EDevice.TYPE]: deviceType(),
      ...(deviceInfos && {
        [EDevice.MODEL]: deviceInfos.model,
        [EDevice.PLATFORM_VERSION]: deviceInfos.platformVersion,
        [EDevice.PLATFORM]: deviceInfos.platform
      })
    };
  },
  businessState() {
    if (!app) return null;

    return app.$store.getters['business/getBusinessState'];
  },
  getIdentifiedId() {
    if (!window.analytics || !window.analytics.user) return;

    return window.analytics.user().id();
  },
  getAnonymousId() {
    if (!window.analytics || !window.analytics.user) return;

    return window.analytics.user().anonymousId();
  },
  utmProperties() {
    if (!window.analytics) return {};

    let data = {};

    const sessionUTMStorage = sessionStorage.getItem(UTM_DATA_STORAGE);

    if (sessionUTMStorage) {
      return JSON.parse(sessionUTMStorage);
    }

    window.analytics.ready(() => {
      const keywords = Object.values(EUtmParamsEvent);
      if (!window.mixpanel || !window.mixpanel.cookie) return data;

      const properties = window.mixpanel.cookie.props;

      for (const keyword of keywords) {
        if (!properties[keyword]) continue;

        Object.assign(data, {
          [keyword]: properties[keyword]
        });
      }
    });

    return data;
  },
  extraInfo() {
    return {
      [EBusinessIdentify.TRACKED_ON]: 'Frontend',
      [EBusinessIdentify.EMBEDDED]: insideProviderIframe(),
      [EBusinessIdentify.SOURCE_CONTEXT]: insideProviderIframe()
        ? 'embedded'
        : 'direct',
      [EBusinessIdentify.APP_SOURCE]: isRemoteLoading()
        ? 'Merchant Portal v2'
        : 'Merchant Portal'
    };
  },
  reset() {
    const resetFunction = () => {
      window.analytics.ready(() => {
        window.analytics.user().anonymousId('');
        window.analytics.user().id('');
        if (window.mixpanel) {
          if (window.mixpanel.cookie) window.mixpanel.cookie.clear();
          window.mixpanel.reset();
        }
        window.analytics.reset();
      });
    };

    if (window.analytics) {
      resetFunction();
    } else {
      const intervalId = setInterval(() => callback(), 100);

      const callback = () => {
        if (window.analytics) {
          clearInterval(intervalId);
          resetFunction();
        }
      };
    }
  },
  page(pageName: string, pageProps?: any, isAnonymousRoute = false) {
    (async () => {
      const formatedPageName = this.normalizeEventLabel(pageName);
      if (!formatedPageName) return;
      const analyticsUserId =
        window.analytics.user && window.analytics.user().id();

      if (analyticsUserId && isAnonymousRoute) {
        this.reset();
      }

      window.analytics.page(
        formatedPageName,
        sanitizeData({
          ...this.extraInfo(),
          ...(await this.getDeviceInfos()),
          ...pageProps
        }),
        undefined,
        () => {
          analyticsDebugger(`[PAGE VIEW]: Viewed ${formatedPageName} Page`);
        }
      );
    })();
  },
  modal(modalName: string, extraData = {}) {
    (async () => {
      const { pathname: path, search, href } = window.location;

      const formatedModalName = this.normalizeEventLabel(modalName);

      const eventData = {
        name: formatedModalName,
        path,
        referrer: document.referrer,
        search,
        title: document.title,
        url: href,
        ...extraData
      };

      const eventLabel = `Viewed ${formatedModalName} Modal`;

      this.track(eventLabel, eventData);
    })();
  },
  track(event: string, extraData = {}) {
    (async () => {
      const data = {
        ...(await this.getDeviceInfos()),
        ...this.extraInfo(),
        ...extraData
      };
      window.analytics.track(event, sanitizeData(data), undefined, () => {
        analyticsDebugger(`[TRACK]: ${event}`);
      });
    })();
  },
  identifyBusiness(data: Record<string, any>) {
    if (isRemoteLoading()) return;

    (async () => {
      const provider =
        app.$store.getters['merchant/getConnectedProviderWithWidgetSettings'];

      const {
        id,
        ownerManager,
        category = {},
        otherCategory,
        manager = null,
        name,
        type,
        businessLocations = [],
        logo
      } = data;

      const formatedOthercategoy = otherCategory
        ? `Other(${otherCategory})`
        : '';

      const eventData = {
        [EBusinessIdentify.TRACKED_ON]: 'Frontend',
        [EBusinessIdentify.NAME]: name,
        [EBusinessIdentify.BUSINESS_ID]: id,
        [EBusinessIdentify.BUSINESS_TYPE]: type,
        [EBusinessIdentify.AVATAR]: logo.publicPath.small,
        [EBusinessIdentify.BUSINESS_CITY_STATE]: Array.from(
          new Set(
            businessLocations.map(({ city, state }: any) => `${city}/${state}`)
          )
        ).join(', '),
        ...(businessLocations.length && data.type !== IBusinessType.ONLINE
          ? {
              [EBusinessIdentify.COUNTRY]: Array.from(
                new Set(
                  businessLocations.map(
                    ({ countryIso2 }: { countryIso2: string }) => countryIso2
                  )
                )
              ).join(', ')
            }
          : {
              [EBusinessIdentify.COUNTRY]: data.countryIso2
            }),
        [EBusinessIdentify.BUSINESS_CATEGORY]: [
          category.name,
          formatedOthercategoy
        ]
          .filter(Boolean)
          .join(', '),
        [EBusinessIdentify.PROVIDER]:
          provider && provider.name ? provider.name : 'None',
        [EBusinessIdentify.OWNER_ID]: ownerManager.id,
        ...(manager && { [EBusinessIdentify.EMAIL]: manager.mail }),
        ...managerNameTraits(manager),
        ...(await this.getDeviceInfos())
      };

      window.analytics.identify(id, eventData, undefined, () => {
        analyticsDebugger(`[IDENTIFY]: ${name}`);
      });
    })();
  }
};

const segmentIoHandler = {
  get(target: typeof segmentIo, prop: keyof typeof segmentIo) {
    if (
      isRemoteLoading()
        ? shouldShowTrackingDebugger() && !trackingEnableIsActive()
        : shouldShowTrackingDebugger() && !trackingDebuggerIsActive()
    ) {
      return () => {};
    }
    const blockList = ['reset', 'page', 'modal', 'track', 'identifyBusiness'];

    const isSuperAdmin = APIService.isSuperAdmin();
    const hasSuperAdminToken = localStorage.getItem(
      `${window.location.host}-${APIService.SUPER_ADMIN}`
    );

    const shouldTrack = !isSuperAdmin && !hasSuperAdminToken;

    if (!shouldTrack && blockList.includes(prop)) {
      return () => {};
    }

    return target[prop];
  }
};

export default new Proxy(segmentIo, segmentIoHandler);
